import { getBundleOverride } from './shared-components/bundle-override/build';

/**
 * List out all the environment variables we use here one by one,
 * so Webpack EnvironmentPlugin can detect + replace them at build time
 * We can't use something like const { BITBUCKET_COMMIT } = process.env
 * @see https://bitbucket.org/atlassian/dev-console/pull-requests/249/
 * This is the only place in the frontend codebase we are be allowed to use 'process.env',
 * hence the ESLint disable rule below
 */
/* eslint-disable no-restricted-globals */
export const env = {
  BITBUCKET_COMMIT: process.env.BITBUCKET_COMMIT,
  BITBUCKET_BUILD_NUMBER: process.env.BITBUCKET_BUILD_NUMBER,
  DEBUG_BROWSER_METRICS: process.env.DEBUG_BROWSER_METRICS,
  LD_CLIENT_KEY: process.env.LD_CLIENT_KEY,
  GRAPHQL_GATEWAY_URL_OVERRIDE: process.env.GRAPHQL_GATEWAY_URL_OVERRIDE,
  JEST_WORKER_ID: process.env.JEST_WORKER_ID,
  REACT_APP_SEGMENT_API_KEY_DEV: process.env.REACT_APP_SEGMENT_API_KEY_DEV,
  REACT_APP_SEGMENT_API_KEY_PROD: process.env.REACT_APP_SEGMENT_API_KEY_PROD,
  ROUTER_BASENAME: process.env.ROUTER_BASENAME,
  SEGMENT_API_KEY_DEV: process.env.SEGMENT_API_KEY_DEV,
  SEGMENT_API_KEY_PROD: process.env.SEGMENT_API_KEY_PROD,
};
/* eslint-enable no-restricted-globals */

export type EnvironmentType = 'local' | 'dev' | 'staging' | 'prod';

// Hostnames must stay in sync with https://extranet.atlassian.com/display/ECO/DAC+-+Feature+Flags
export const productionHostnames = [
  'dac.prod.atl-paas.net',
  'dac.us-east-1.prod.atl-paas.net',
  'developer.atlassian.com',
  'dev-console.us-west-2.prod.public.atl-paas.net',
];

export const stagingHostnames = [
  'dac.staging.atl-paas.net',
  'dac.us-east-1.staging.atl-paas.net',
  'developer.stg.internal.atlassian.com',
  'dev-console.us-west-2.staging.public.atl-paas.net',
];

export const devHostnames = [
  'dac.dev.atl-paas.net',
  'dac--app.ap-southeast-2.dev.atl-paas.net',
  'dev-console.us-west-2.dev.public.atl-paas.net',
];

function isHTMLScriptElement(element: HTMLOrSVGScriptElement): element is HTMLScriptElement {
  return 'src' in element;
}

function getHostname(resolvedHostURL: string, fallbackHostname: string): string {
  try {
    const resolvedHostUrlObj = new URL(resolvedHostURL);
    return resolvedHostUrlObj.hostname;
  } catch {
    return fallbackHostname;
  }
}

/**
 * Returns the environment depending on the hostname.
 *
 * @param {string} [hostname=window.location.hostname] the hostname
 * @returns {string} one of 'prod', 'staging', 'dev', or 'local'.
 */
export function environment(hostname: string = window.location.hostname): EnvironmentType {
  const resolvedHostURL =
    document.currentScript && isHTMLScriptElement(document.currentScript) ? document.currentScript.src : hostname;

  const resolvedHostname = getHostname(resolvedHostURL, hostname);

  if (getBundleOverride(location.search)) {
    return 'dev';
  }

  if (productionHostnames.includes(resolvedHostname)) {
    return 'prod';
  }

  if (stagingHostnames.includes(resolvedHostname)) {
    return 'staging';
  }

  if (devHostnames.includes(resolvedHostname)) {
    return 'dev';
  }

  return 'local';
}

export function getMarketplaceBaseUrl(): string {
  return `https://${
    environment() === 'prod' ? 'marketplace-cdn.atlassian.com' : 'marketplace.stg.internal.atlassian.com'
  }`;
}

export function bitbucketVersion(): string {
  return env.BITBUCKET_COMMIT && env.BITBUCKET_BUILD_NUMBER
    ? `${env.BITBUCKET_COMMIT}:${env.BITBUCKET_BUILD_NUMBER}`
    : 'local-dev';
}

type EnvWindow = Window & {
  process: {
    env?: Record<string, unknown>;
  };
};

declare const window: EnvWindow;

/**
 * All dev-console modules should import from env, but for any third party dependencies that still rely on process.env,
 * we can polyfill it for them
 */
function polyfillGlobalProcessEnv() {
  if (!window.process) {
    window.process = {};
  }
  if (!window.process.env) {
    window.process.env = {};
  }
  window.process.env = {
    ...window.process.env,
    ...env,
  };
}
polyfillGlobalProcessEnv();
